import type { CityLayout, PoiKind } from '../world/types';
import type { MapMarker, MarkerKind, SearchArea } from './uiTypes';
import { legendMatches, type LegendItem } from './mapLegend';
import { drawOrder, MARKER_STYLE, rimPins, SCHEME } from './markerScheme';
import type { MapCamera, TapCandidate } from './mapView';
import { drawMapIcon, iconFor, loadMapIcons, mapIconsReady } from './mapIcons';
import { distanceText } from './ownCar';
import { bakeCity, bakedBytes, drawRoofDetail, mapLookFromUrl, PALETTES, roofDetailAlpha, type MapLook, type MapPalette } from './mapStyles';
import { cssSize } from '../render/cssSize';

/**
 * Minimap renderer. The static city (blocks, roads) is baked once into an offscreen bitmap from the
 * CityLayout; each frame we only blit a rotated window of it and draw markers on top.
 *
 * Math: bitmap px = (world - boundsMin) * S. The window is rotated so that world direction
 * `forward(heading)` points up the screen: canvas rotation = heading - PI.
 */

const PAPER = '#e9e0c9';
/** The soft dark halo under every marker (one icon family with an outline). */
const HALO = 'rgba(6,8,11,0.5)';
const INK = '#15120f';
const RED = '#d03420';
const BLUE = '#3566e0';
const MUSTARD = '#dba73f';

export const POI_GLYPH: Partial<Record<PoiKind, string>> = {
  resprayShop: 'R',
  garage: 'G',
  lockup: 'L',
  safehouse: 'S',
  policeStation: '!',
};

export interface MapView {
  /** World point at the centre of the map. */
  x: number;
  z: number;
  /** World heading drawn as "up". PI = north up (default camera). */
  heading: number;
  /** Metres from centre to edge of the circle. */
  radius: number;
}

export interface MapPlayer {
  x: number;
  z: number;
  heading: number;
  /** Driving: the car's paint (CSS) — the arrow takes it, so the arrow IS your car (ownCar.ts). */
  tint?: string | null;
}

/** The planned route (src/onboarding/route.ts RoutePath) as the map draws it: a polyline in the goal's colour. */
export interface MapRoute {
  xs: Float32Array;
  zs: Float32Array;
  count: number;
  colour: string;
}

export class Minimap {
  /** Marker kinds the key's switches took off the map (mapLegend.ts hiddenKinds). */
  private hidden: ReadonlySet<MarkerKind> = new Set();
  private baked: HTMLCanvasElement | null = null;
  /** The half-size copy the minimap samples (mapStyles.ts), and its px per metre. */
  private mini: HTMLCanvasElement | null = null;
  private miniS = 1;
  /** Bytes the baked bitmaps take (the perf read-out). */
  bakedBytes = 0;
  /** The map's look (mapStyles.ts) and the colours its captions draw in. */
  readonly look: MapLook = mapLookFromUrl();
  private readonly pal: MapPalette = PALETTES[this.look];
  // per-frame scratch: rim pins and drawing order (markerScheme.ts)
  private readonly pins = new Set<number>();
  private readonly order: number[] = [];
  /** Bitmap pixels per metre. */
  private S = 2;
  private readonly minX: number;
  private readonly minZ: number;
  private readonly poiMarkers: MapMarker[];
  /** Radius (px) of a disc that carries a map icon: bigger on the small round minimap than on the full map. */
  private iconR = ICON_DISC;
  /** Draw icons from the bold atlas (the minimap). */
  private boldIcons = false;
  /** Icon markers the minimap's declutter left out this frame: drawn as a small plain disc instead. */
  private readonly small = new Set<MapMarker>();
  private readonly cand: { m: MapMarker; x: number; y: number; rank: number }[] = [];

  constructor(private readonly layout: CityLayout) {
    this.minX = layout.bounds.minX;
    this.minZ = layout.bounds.minZ;
    this.poiMarkers = layout.pois
      .filter((p) => POI_GLYPH[p.kind])
      .map((p) => ({ x: p.pos.x, z: p.pos.z, kind: 'poi' as const, glyph: POI_GLYPH[p.kind]!, pin: false }));
    loadMapIcons();
    // Canvas text never triggers a webfont download by itself: fetch the symbol fallback up front so
    // marker glyphs like the phone-box ☎ draw in it rather than whatever the system has.
    try {
      void document.fonts?.load('10px "BH Symbols"', '☎♪').catch(() => undefined);
    } catch {
      /* no FontFaceSet (tests) */
    }
  }

  /** Paint the city for the look into the offscreen bitmap (mapStyles.ts). Call once after the layout exists. */
  bake(): void {
    const b = bakeCity(this.layout, this.look);
    if (!b) return;
    this.baked = b.canvas;
    this.S = b.S;
    this.mini = b.mini;
    this.miniS = b.miniS;
    this.bakedBytes = bakedBytes(b);
    document.documentElement.dataset.maplook = this.look;
  }

  /** The key's switches: these kinds are left off both maps (and off the rim and the tap targets). */
  setHidden(kinds: ReadonlySet<MarkerKind>): void {
    this.hidden = kinds;
  }

  // ---------------------------------------------------------------- per-frame

  /** Round minimap. `canvas` is the HUD canvas (any size; draws in its pixel space). */
  draw(canvas: HTMLCanvasElement, view: MapView, player: MapPlayer, markers: readonly MapMarker[], search: SearchArea | null, time: number, route: MapRoute | null = null): void {
    const g = canvas.getContext('2d');
    if (!g || !this.baked) return;
    const size = canvas.width;
    const R = size / 2;
    const k = size / 230; // marker scale
    const mpp = R / view.radius; // screen px per metre
    const rot = view.heading - Math.PI;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    this.carDistance = true;
    this.viewX = view.x;
    this.viewZ = view.z;

    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, size, size);
    g.save();
    g.beginPath();
    g.arc(R, R, R, 0, Math.PI * 2);
    g.clip();
    g.fillStyle = this.pal.bg;
    g.fillRect(0, 0, size, size);

    // City bitmap.
    g.save();
    g.translate(R, R);
    g.rotate(rot);
    // The half-size copy: a quarter of the texels to sample each frame, and the roofs average to a calm tone.
    const bmp = this.mini ?? this.baked;
    const bS = this.mini ? this.miniS : this.S;
    const sc = mpp / bS;
    g.scale(sc, sc);
    g.translate(-(view.x - this.minX) * bS, -(view.z - this.minZ) * bS);
    g.imageSmoothingEnabled = true;
    g.drawImage(bmp, 0, 0);
    g.restore();

    const toScreen = (wx: number, wz: number, out: { x: number; y: number }): void => {
      const dx = wx - view.x;
      const dz = wz - view.z;
      out.x = R + (dx * cos - dz * sin) * mpp;
      out.y = R + (dx * sin + dz * cos) * mpp;
    };
    const p = { x: 0, y: 0 };

    // Search area.
    if (search) {
      toScreen(search.x, search.z, p);
      const rr = search.radius * mpp;
      const pulse = 0.5 + 0.5 * Math.sin(time * 4);
      g.fillStyle = `rgba(208,52,32,${0.1 + 0.07 * pulse})`;
      g.beginPath();
      g.arc(p.x, p.y, rr, 0, Math.PI * 2);
      g.fill();
      g.setLineDash([8 * k, 6 * k]);
      g.lineDashOffset = -time * 14;
      g.lineWidth = 2.2 * k;
      g.strokeStyle = `rgba(255,120,95,${0.65 + 0.3 * pulse})`;
      g.stroke();
      g.setLineDash([]);
    }

    // Canvas px per CSS px: a phone's minimap is under 110 CSS px across, so the things that must read
    // (icons, your car, the route line) are sized in CSS px, not in the canvas's.
    const cssW = cssSize(canvas).w; // cached: no layout read per frame (render/cssSize.ts)
    const cssPx = cssW > 0 ? size / cssW : 1;

    // The route to the goal (or to your mark / your car) as a line on the road, under the markers.
    if (route) this.drawRoute(g, route, toScreen, p, Math.max(3 * k, MINI_ROUTE_CSS * cssPx), 1);

    // Static POIs, then the live markers lowest priority first (the goal on top); only the markers
    // rimPins chose are pinned to the rim when out of range (markerScheme.ts). A hidden kind counts as
    // "in range" so it takes no rim place.
    const edge = R - 12 * k;
    const inRange = (m: MapMarker): boolean => {
      if (this.hidden.has(m.kind)) return true;
      toScreen(m.x, m.z, p);
      return Math.hypot(p.x - R, p.y - R) <= edge;
    };
    rimPins(markers, view.x, view.z, inRange, this.pins);
    // Icon discs: at least MINI_ICON_CSS_R CSS px whatever the minimap's size (a phone's is under 100 CSS px
    // across), from the bold atlas, and only the few that matter (declutter).
    this.iconR = Math.max(ICON_DISC_MINI * k, MINI_ICON_CSS_R * cssPx);
    // Your car's plate at least MINI_CAR_CSS wide, its distance at least MINI_DIST_CSS px of type.
    this.carK = Math.max(k, (MINI_CAR_CSS / 26) * cssPx);
    this.distPx = Math.max(8.5 * k, MINI_DIST_CSS * cssPx);
    this.boldIcons = true;
    this.routedCar = routedCar(markers);
    this.declutter(markers, toScreen, p, R, edge);
    for (const m of this.poiMarkers) this.drawMarker(g, m, toScreen, p, R, R, edge, k, time);
    for (const i of drawOrder(markers, this.order)) this.drawMarker(g, markers[i]!, toScreen, p, R, R, edge, k, time, this.pins.has(i));

    // Player arrow, with a faint cone the way you face (so the arrow's heading reads at a glance).
    const fx = Math.sin(player.heading);
    const fz = Math.cos(player.heading);
    const ax = fx * cos - fz * sin;
    const ay = fx * sin + fz * cos;
    const pa = Math.atan2(ay, ax);
    this.drawCone(g, R, R, pa, Math.max(40 * k, 0.33 * R));
    this.drawArrow(g, R, R, pa, 11 * k, player.tint);

    g.restore();

    // North tick on the rim.
    const nx = 0 * cos - -1 * sin; // north = (0,-1)
    const ny = 0 * sin + -1 * cos;
    const na = Math.atan2(ny, nx);
    const tx = R + Math.cos(na) * (R - 13 * k);
    const ty = R + Math.sin(na) * (R - 13 * k);
    g.fillStyle = RED;
    g.strokeStyle = INK;
    g.lineWidth = 2 * k;
    g.beginPath();
    g.arc(tx, ty, 8.5 * k, 0, Math.PI * 2);
    g.fill();
    g.stroke();
    g.fillStyle = PAPER;
    g.font = `${11 * k}px "BH Anton", "BH Symbols", Impact, sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('N', tx, ty + 0.6 * k);
  }

  private drawMarker(
    g: CanvasRenderingContext2D,
    m: MapMarker,
    toScreen: (x: number, z: number, out: { x: number; y: number }) => void,
    p: { x: number; y: number },
    cx: number,
    cy: number,
    edge: number,
    k: number,
    time: number,
    rimOK = false,
  ): void {
    if (m.area || this.hidden.has(m.kind)) return; // areas are drawn by drawFull's area pass only
    // A route to your car: the flag would sit on the car's plate; the plate takes the flag's plum edge instead.
    if (m.kind === 'mark' && this.routedCar) return;
    toScreen(m.x, m.z, p);
    let x = p.x;
    let y = p.y;
    const dx = x - cx;
    const dy = y - cy;
    const d = Math.hypot(dx, dy);
    let pinned = false;
    if (d > edge) {
      if (!rimOK) return;
      x = cx + (dx / d) * edge;
      y = cy + (dy / d) * edge;
      pinned = true;
    }
    const st = MARKER_STYLE[m.kind];
    const col = st.colour;

    // Your car: its silhouette on a paper plate; on the rim a size smaller with how far it is (in metres).
    if (st.shape === 'car') {
      const ang = Math.atan2(dy, dx);
      const ck = pinned ? this.carK * 0.8 : this.carK;
      if (pinned) {
        // The ink tick pointing out, just past the plate's edge.
        const t = 9.5 * ck;
        g.save();
        g.translate(x, y);
        g.rotate(ang + Math.PI / 2);
        g.fillStyle = INK;
        g.beginPath();
        g.moveTo(0, -t - 7 * k);
        g.lineTo(4.5 * k, -t + 1 * k);
        g.lineTo(-4.5 * k, -t + 1 * k);
        g.closePath();
        g.fill();
        g.restore();
      }
      this.drawCar(g, x, y, ck, m.tint ?? col, m.glyph === '★', m === this.routedCar);
      if (pinned && this.carDistance) {
        // Where it is from you: the view's centre is you on the minimap. The text and its font are made
        // only when they change (a stroked text each frame cost a tenth of a millisecond on a phone); the
        // ink behind it is a small plate, not a stroke.
        const metres = Math.hypot(m.x - this.viewX, m.z - this.viewZ);
        const tenMetres = Math.round(metres / 10);
        if (tenMetres !== this.distTen) {
          this.distTen = tenMetres;
          this.distText = distanceText(metres);
          this.distW = 0;
        }
        if (this.distPx !== this.distFontPx) {
          this.distFontPx = this.distPx;
          this.distFont = `bold ${this.distPx}px "BH Elite", "BH Symbols", "Courier New", monospace`;
          this.distW = 0;
        }
        g.font = this.distFont;
        if (this.distW === 0) this.distW = g.measureText(this.distText).width;
        const tx = x - Math.cos(ang) * (9 * ck + this.distPx * 0.8);
        const ty = y - Math.sin(ang) * (9 * ck + this.distPx * 0.8);
        const h = this.distPx;
        g.fillStyle = 'rgba(21,18,15,0.78)';
        g.fillRect(tx - this.distW / 2 - 2 * k, ty - h * 0.6, this.distW + 4 * k, h * 1.2);
        g.textAlign = 'center';
        g.textBaseline = 'middle';
        g.fillStyle = PAPER;
        g.fillText(this.distText, tx, ty);
      }
      return;
    }

    // The pulse ring (the goal; a live event; something that needs you), never on the rim.
    if (m.pulse && !pinned && m.kind !== 'police') {
      const t = (time * 0.9) % 1;
      g.strokeStyle = col;
      g.globalAlpha = 1 - t;
      g.lineWidth = 2.4 * k;
      g.beginPath();
      g.arc(x, y, (8 + t * 18) * k, 0, Math.PI * 2);
      g.stroke();
      g.globalAlpha = 1;
    }

    if (pinned) {
      // On the rim: the goal is a big red arrowhead; anything else keeps its own shape, a size smaller,
      // with a small ink tick pointing out to where it is.
      const ang = Math.atan2(dy, dx);
      g.save();
      g.translate(x, y);
      g.rotate(ang + Math.PI / 2);
      if (m.kind === 'objective') {
        g.fillStyle = col;
        g.strokeStyle = INK;
        g.lineWidth = 2 * k;
        g.beginPath();
        g.moveTo(0, -9 * k);
        g.lineTo(8 * k, 6 * k);
        g.lineTo(-8 * k, 6 * k);
        g.closePath();
        g.fill();
        g.stroke();
        g.restore();
        return;
      }
      g.fillStyle = INK;
      g.beginPath();
      g.moveTo(0, -12 * k);
      g.lineTo(4.5 * k, -6 * k);
      g.lineTo(-4.5 * k, -6 * k);
      g.closePath();
      g.fill();
      g.restore();
      k *= 0.85;
    }

    switch (st.shape) {
      case 'dot': {
        const alt = m.alert ? Math.floor(time * 5) % 2 === 0 : false;
        g.fillStyle = m.alert ? (alt ? RED : BLUE) : col;
        g.strokeStyle = INK;
        g.lineWidth = 1.8 * k;
        g.beginPath();
        g.arc(x, y, 5.2 * k, 0, Math.PI * 2);
        g.fill();
        g.stroke();
        return;
      }
      case 'disc': {
        // A place or a person with work that has a map icon (mapIcons.ts): the same disc a size bigger with
        // the pictogram in it; the glyph letter until the atlas is in (or if it never comes).
        // One the minimap's declutter left out is a small plain disc (a letter that size wouldn't read).
        const small = this.small.has(m);
        const icon = m.glyph && !small && mapIconsReady() ? iconFor(m.kind, m.glyph) : null;
        const r = icon ? this.iconR * (pinned ? 0.85 : 1) : (small ? 4.6 : 6.6) * k;
        g.beginPath();
        g.arc(x, y, r, 0, Math.PI * 2);
        // one family: every disc sits in a soft dark halo, then its ink edge (reads on the pale roads too)
        g.strokeStyle = HALO;
        g.lineWidth = 4.2 * k;
        g.stroke();
        g.fillStyle = col;
        g.strokeStyle = INK;
        g.lineWidth = 1.6 * k;
        g.fill();
        g.stroke();
        if (icon) drawMapIcon(g, icon, x, y, r * 2, this.boldIcons);
        else if (m.glyph && !small) this.glyph(g, m.glyph, x, y, 8.5 * k, INK);
        return;
      }
      case 'hollowDisc': {
        g.fillStyle = INK;
        g.strokeStyle = col;
        g.lineWidth = 2.4 * k;
        g.beginPath();
        g.arc(x, y, 6.4 * k, 0, Math.PI * 2);
        g.fill();
        g.stroke();
        if (m.glyph) this.glyph(g, m.glyph, x, y, 8.5 * k, col);
        return;
      }
      case 'square': {
        // Another player: a stone square; the danger colour while wanted.
        g.strokeStyle = HALO;
        g.lineWidth = 4.2 * k;
        g.strokeRect(x - 5.6 * k, y - 5.6 * k, 11.2 * k, 11.2 * k);
        g.fillStyle = m.alert ? SCHEME.danger : col;
        g.strokeStyle = INK;
        g.lineWidth = 1.6 * k;
        g.fillRect(x - 5.6 * k, y - 5.6 * k, 11.2 * k, 11.2 * k);
        g.strokeRect(x - 5.6 * k, y - 5.6 * k, 11.2 * k, 11.2 * k);
        if (m.glyph) this.glyph(g, m.glyph, x, y, 8 * k, INK);
        return;
      }
      case 'ring': {
        // The suggestion: a dashed paper ring round the place (its own marker shows through) — outside an
        // icon disc, however big the minimap draws those.
        const r = pinned ? 6 * k : Math.max(11 * k, this.iconR + 3 * k);
        g.strokeStyle = INK;
        g.lineWidth = 4.2 * k;
        g.beginPath();
        g.arc(x, y, r, 0, Math.PI * 2);
        g.stroke();
        g.strokeStyle = col;
        g.lineWidth = 2.2 * k;
        g.setLineDash(this.dash(4 * k, 3 * k));
        g.lineDashOffset = -time * 6 * k;
        g.stroke();
        g.setLineDash(NO_DASH);
        return;
      }
      case 'flag': {
        this.drawFlag(g, x - 4 * k, y + 8 * k, k * 0.62, col);
        return;
      }
      case 'triangle': {
        g.beginPath();
        g.moveTo(x, y - 9 * k);
        g.lineTo(x + 9 * k, y + 7 * k);
        g.lineTo(x - 9 * k, y + 7 * k);
        g.closePath();
        g.strokeStyle = HALO;
        g.lineWidth = 4.6 * k;
        g.lineJoin = 'round';
        g.stroke();
        g.fillStyle = col;
        g.strokeStyle = INK;
        g.lineWidth = 2 * k;
        g.fill();
        g.stroke();
        this.glyph(g, m.glyph ?? '!', x, y + 1.5 * k, 8.5 * k, INK);
        return;
      }
      case 'diamond':
      case 'hollowDiamond': {
        const hollow = st.shape === 'hollowDiamond';
        g.save();
        g.translate(x, y);
        g.rotate(Math.PI / 4);
        g.strokeStyle = HALO;
        g.lineWidth = 4.6 * k;
        g.strokeRect(-6.5 * k, -6.5 * k, 13 * k, 13 * k);
        g.fillStyle = hollow ? INK : col;
        g.strokeStyle = hollow ? col : INK;
        g.lineWidth = (hollow ? 2.6 : 2) * k;
        g.fillRect(-6.5 * k, -6.5 * k, 13 * k, 13 * k);
        g.strokeRect(-6.5 * k, -6.5 * k, 13 * k, 13 * k);
        g.restore();
        if (m.glyph) this.glyph(g, m.glyph, x, y, 9.5 * k, PAPER);
        return;
      }
    }
  }

  /**
   * The minimap's icon declutter: of the discs that would carry an icon, the live ones (pulsing first) and
   * then the nearest places keep it — at most MINI_ICONS, none overlapping one already kept; the rest
   * (`small`) are drawn as small plain discs, so the place still shows but doesn't crowd the map.
   */
  private declutter(
    markers: readonly MapMarker[],
    toScreen: (x: number, z: number, out: { x: number; y: number }) => void,
    p: { x: number; y: number },
    R: number,
    edge: number,
  ): void {
    this.small.clear();
    if (!mapIconsReady()) return;
    const c = this.cand;
    let n = 0;
    const consider = (m: MapMarker, live: boolean, rimOK: boolean): void => {
      if (m.area || this.hidden.has(m.kind) || MARKER_STYLE[m.kind].shape !== 'disc' || !iconFor(m.kind, m.glyph)) return;
      toScreen(m.x, m.z, p);
      const dx = p.x - R;
      const dy = p.y - R;
      const d = Math.hypot(dx, dy);
      if (d > edge) {
        if (!rimOK) return;
        p.x = R + (dx / d) * edge;
        p.y = R + (dy / d) * edge;
      }
      const e = c[n] ?? (c[n] = { m, x: 0, y: 0, rank: 0 });
      e.m = m;
      e.x = p.x;
      e.y = p.y;
      e.rank = (live ? (m.pulse ? 0 : 1) : 2) * 1e6 + d;
      n++;
    };
    for (const m of this.poiMarkers) consider(m, false, false);
    for (let i = 0; i < markers.length; i++) consider(markers[i]!, true, this.pins.has(i));
    c.length = n;
    c.sort((a, b) => a.rank - b.rank);
    const minD = this.iconR * 1.7;
    let shown = 0;
    for (let i = 0; i < n; i++) {
      let ok = shown < MINI_ICONS;
      for (let j = 0; j < i && ok; j++) if (!this.small.has(c[j]!.m) && Math.hypot(c[j]!.x - c[i]!.x, c[j]!.y - c[i]!.y) < minD) ok = false;
      if (ok) shown++;
      else this.small.add(c[i]!.m);
    }
  }

  private glyph(g: CanvasRenderingContext2D, text: string, x: number, y: number, size: number, colour: string): void {
    g.fillStyle = colour;
    g.font = `${size}px "BH Anton", "BH Symbols", Impact, sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(text, x, y + size * 0.06);
  }

  /**
   * You. Paper with an ink edge on foot; in a car the arrow is filled with the car's paint and edged in paper,
   * so the car you drive and the car you left read as the same thing.
   */
  private drawArrow(g: CanvasRenderingContext2D, x: number, y: number, angle: number, size: number, tint?: string | null): void {
    g.save();
    g.translate(x, y);
    g.rotate(angle + Math.PI / 2); // arrow drawn pointing up
    g.lineJoin = 'round';
    g.beginPath();
    g.moveTo(0, -size);
    g.lineTo(size * 0.72, size * 0.8);
    g.lineTo(0, size * 0.38);
    g.lineTo(-size * 0.72, size * 0.8);
    g.closePath();
    if (tint) {
      g.strokeStyle = INK;
      g.lineWidth = size * 0.46;
      g.stroke();
      g.strokeStyle = PAPER;
      g.lineWidth = size * 0.24;
      g.stroke();
      g.fillStyle = tint;
      g.fill();
    } else {
      g.fillStyle = PAPER;
      g.strokeStyle = INK;
      g.lineWidth = size * 0.2;
      g.fill();
      g.stroke();
    }
    g.restore();
  }

  /** A faint wedge the way you face (±CONE_HALF), under the arrow: the heading reads without a second look. */
  private drawCone(g: CanvasRenderingContext2D, x: number, y: number, angle: number, len: number): void {
    g.fillStyle = CONE;
    g.beginPath();
    g.moveTo(x, y);
    g.arc(x, y, len, angle - CONE_HALF, angle + CONE_HALF);
    g.closePath();
    g.fill();
  }

  /**
   * A car seen from the side (the blip every driving game taught people), `k` scaling it, filled with the
   * car's paint on a paper plate with an ink edge — so a black car reads on the dark map and a white one
   * on the plate. `motor`: a mustard ★ badge, the car you keep (ownCar.ts).
   */
  private drawCar(g: CanvasRenderingContext2D, x: number, y: number, k: number, paint: string, motor: boolean, routed = false): void {
    g.save();
    g.translate(x, y);
    g.lineJoin = 'round';
    // the plate (a plum edge while your route leads to it: the flag and the car are one thing)
    g.fillStyle = PAPER;
    g.strokeStyle = INK;
    g.lineWidth = 1.6 * k;
    roundRect(g, -13 * k, -8.5 * k, 26 * k, 17 * k, 3.5 * k);
    g.fill();
    g.stroke();
    if (routed) {
      g.strokeStyle = MARKER_STYLE.mark.colour;
      g.lineWidth = 2.6 * k;
      roundRect(g, -14.5 * k, -10 * k, 29 * k, 20 * k, 4.5 * k);
      g.stroke();
    }
    // the body
    g.fillStyle = paint;
    g.lineWidth = 1.3 * k;
    g.beginPath();
    g.moveTo(-10.5 * k, 3 * k);
    g.lineTo(-10.5 * k, -0.5 * k);
    g.lineTo(-7 * k, -1.5 * k);
    g.lineTo(-4 * k, -5.5 * k);
    g.lineTo(4 * k, -5.5 * k);
    g.lineTo(7.5 * k, -1.5 * k);
    g.lineTo(10.5 * k, -0.5 * k);
    g.lineTo(10.5 * k, 3 * k);
    g.closePath();
    g.fill();
    g.stroke();
    // the windows (a lighter line so the roof reads as a roof)
    g.strokeStyle = 'rgba(233,224,201,0.55)';
    g.lineWidth = 1.1 * k;
    g.beginPath();
    g.moveTo(-5.6 * k, -1.6 * k);
    g.lineTo(-3.2 * k, -4.4 * k);
    g.lineTo(3.2 * k, -4.4 * k);
    g.lineTo(6 * k, -1.6 * k);
    g.stroke();
    // the wheels
    g.fillStyle = INK;
    g.beginPath();
    g.arc(-6 * k, 3.4 * k, 2.6 * k, 0, Math.PI * 2);
    g.arc(6 * k, 3.4 * k, 2.6 * k, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = PAPER;
    g.beginPath();
    g.arc(-6 * k, 3.4 * k, 1 * k, 0, Math.PI * 2);
    g.arc(6 * k, 3.4 * k, 1 * k, 0, Math.PI * 2);
    g.fill();
    if (motor) {
      g.fillStyle = MUSTARD;
      g.strokeStyle = INK;
      g.lineWidth = 1.2 * k;
      g.beginPath();
      g.arc(11 * k, -7.5 * k, 4.2 * k, 0, Math.PI * 2);
      g.fill();
      g.stroke();
      this.glyph(g, '★', 11 * k, -7.3 * k, 5.6 * k, INK);
    }
    g.restore();
  }

  /** The route as a line on the road: an ink under-stroke so it reads on the pale roads too. */
  private drawRoute(
    g: CanvasRenderingContext2D,
    route: MapRoute,
    toScreen: (x: number, z: number, out: { x: number; y: number }) => void,
    p: { x: number; y: number },
    width: number,
    alpha: number,
  ): void {
    if (route.count < 2) return;
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.beginPath();
    for (let i = 0; i < route.count; i++) {
      toScreen(route.xs[i]!, route.zs[i]!, p);
      if (i === 0) g.moveTo(p.x, p.y);
      else g.lineTo(p.x, p.y);
    }
    g.globalAlpha = alpha * 0.7;
    g.strokeStyle = INK;
    g.lineWidth = width * 2.1;
    g.stroke();
    g.globalAlpha = alpha;
    g.strokeStyle = route.colour;
    g.lineWidth = width;
    g.stroke();
    g.globalAlpha = 1;
  }


  // ---------------------------------------------------------------- full map

  /**
   * The full map (north up, zoomable): the baked city scaled by `cam`, then areas, names, markers, your mark
   * and labels. Drawn in CSS px (`dpr` scales the buffer). Called only when the view or the markers changed
   * (FullMapScreen throttles it); nothing is allocated per draw.
   */
  drawFull(
    canvas: HTMLCanvasElement,
    cam: MapCamera,
    dpr: number,
    player: MapPlayer,
    markers: readonly MapMarker[],
    search: SearchArea | null,
    time: number,
    mark: { x: number; z: number; label: string } | null,
    flash: { kind: LegendItem['kind']; glyph?: string; age: number } | null,
    pick: { x: number; z: number } | null = null,
    route: MapRoute | null = null,
    reserve: readonly { x0: number; y0: number; x1: number; y1: number }[] = NO_RESERVE,
  ): void {
    const g = canvas.getContext('2d');
    if (!g || !this.baked) return;
    const L = this.layout;
    const W = cam.w;
    const H = cam.h;
    const z = cam.zoom;
    this.cam = cam;
    this.carDistance = false;
    this.routedCar = routedCar(markers);
    // Marker size: a touch bigger on a big screen, and a little as you zoom in.
    const rel = z / cam.minZoom;
    const k = Math.min(1.7, Math.max(1.1, 0.95 + H / 1100) * (1 + Math.min(0.25, (rel - 1) * 0.05)));
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.fillStyle = this.pal.bg;
    g.fillRect(0, 0, W, H);
    g.imageSmoothingEnabled = true;
    const bs = z / this.S;
    g.drawImage(this.baked, cam.toScreenX(this.minX), cam.toScreenY(this.minZ), this.baked.width * bs, this.baked.height * bs);
    // The roofs' detail comes in as you zoom in (night only; mapStyles.ts): calm far out, a picture up close.
    if (this.look === 'night') drawRoofDetail(g, L, cam, roofDetailAlpha(z / cam.minZoom));
    this.labelCount = 0;

    // Areas under everything (online: district leaders' colours, contested districts).
    for (const m of markers) {
      const a = m.area;
      if (!a) continue;
      const x0 = cam.toScreenX(a.minX);
      const y0 = cam.toScreenY(a.minZ);
      const w = (a.maxX - a.minX) * z;
      const h = (a.maxZ - a.minZ) * z;
      g.globalAlpha = a.pulse ? 0.65 + 0.35 * Math.sin(time * 4) : 1;
      g.fillStyle = a.fill;
      g.fillRect(x0, y0, w, h);
      if (a.stroke) {
        g.strokeStyle = a.stroke;
        g.lineWidth = 3 * k;
        if (a.dashed) {
          g.setLineDash(this.dash(10 * k, 7 * k));
          g.lineDashOffset = -time * 12;
        }
        g.strokeRect(x0 + 2 * k, y0 + 2 * k, w - 4 * k, h - 4 * k);
        g.setLineDash(NO_DASH);
      }
      g.globalAlpha = 1;
    }
    if (search) {
      g.fillStyle = 'rgba(208,52,32,0.18)';
      g.strokeStyle = 'rgba(255,120,95,0.8)';
      g.lineWidth = 2 * k;
      g.setLineDash(this.dash(8 * k, 6 * k));
      g.beginPath();
      g.arc(cam.toScreenX(search.x), cam.toScreenY(search.z), search.radius * z, 0, Math.PI * 2);
      g.fill();
      g.stroke();
      g.setLineDash(NO_DASH);
    }

    // District names: in the middle of each district's part on screen, big; fainter once zoomed in.
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineJoin = 'round';
    const dSize = Math.round(Math.min(26, Math.max(15, 13 + rel * 2.2)));
    const pal = this.pal;
    this.districtN = 0;
    g.font = `${pal.districtFont === pal.textFont ? '600 ' : ''}${dSize}px ${pal.districtFont}`;
    if ('letterSpacing' in g) g.letterSpacing = pal.districtFont === pal.textFont ? '0.14em' : '0px';
    for (const d of L.districts) {
      const x0 = Math.max(0, cam.toScreenX(d.bounds.minX));
      const x1 = Math.min(W, cam.toScreenX(d.bounds.maxX));
      const y0 = Math.max(0, cam.toScreenY(d.bounds.minZ));
      const y1 = Math.min(H, cam.toScreenY(d.bounds.maxZ));
      if (x1 - x0 < 90 || y1 - y0 < dSize * 1.6) continue;
      const cx = (x0 + x1) / 2;
      const cy = (y0 + y1) / 2;
      const name = pal.districtCaps ? d.name.toUpperCase() : d.name;
      // not under the knobs, the pill, the compass or the scale bar
      const dw0 = g.measureText(name).width / 2;
      let under = false;
      for (const r of reserve) if (cx - dw0 < r.x1 && cx + dw0 > r.x0 && cy - dSize * 0.55 < r.y1 && cy + dSize * 0.55 > r.y0) under = true;
      if (under) continue;
      g.lineWidth = 4;
      g.strokeStyle = pal.districtHalo;
      g.strokeText(name, cx, cy);
      g.fillStyle = pal.district;
      g.globalAlpha = rel > 3 ? 0.6 : 1;
      g.fillText(name, cx, cy);
      g.globalAlpha = 1;
      // the name's box: claimed after the goal's captions (those win), before the rest and the street names
      if (this.districtN < DISTRICT_BOXES) {
        const o = this.districtN++ * 4;
        this.districtBox[o] = cx - dw0;
        this.districtBox[o + 1] = cy - dSize * 0.55;
        this.districtBox[o + 2] = cx + dw0;
        this.districtBox[o + 3] = cy + dSize * 0.55;
      }
    }
    if ('letterSpacing' in g) g.letterSpacing = '0px';
    // Area captions (under the district name): who runs it, a contest.
    g.font = `${Math.round(12 * k)}px "BH Anton", "BH Symbols", Impact, sans-serif`;
    for (const m of markers) {
      const a = m.area;
      if (!a?.caption) continue;
      const cx = cam.toScreenX((a.minX + a.maxX) / 2);
      const cy = cam.toScreenY((a.minZ + a.maxZ) / 2) + dSize;
      g.lineWidth = 3 * k;
      g.strokeStyle = 'rgba(21,18,15,0.85)';
      g.strokeText(a.caption, cx, cy);
      g.fillStyle = PAPER;
      g.fillText(a.caption, cx, cy);
    }

    // The route on the road, under the markers.
    const p = this.pt;
    if (route) this.drawRoute(g, route, this.fullToScreen, p, 3 * k, 0.9);

    // Markers. The flashed key line (a tap on the legend) gets its rings first, so the markers sit on top.
    if (flash) this.drawFlashes(g, cam, markers, mark, player, flash, k);
    this.iconR = ICON_DISC * k;
    this.carK = k * 1.15;
    this.boldIcons = false;
    this.small.clear();
    for (const m of this.poiMarkers) this.drawMarker(g, m, this.fullToScreen, p, 0, 0, Infinity, k, time);
    for (const i of drawOrder(markers, this.order)) {
      const m = markers[i]!;
      if (m.kind !== 'mark') this.drawMarker(g, m, this.fullToScreen, p, 0, 0, Infinity, k, time);
    }
    if (mark && !this.routedCar) this.drawFlag(g, cam.toScreenX(mark.x), cam.toScreenY(mark.z), k, MARKER_STYLE.mark.colour);
    // The place just tapped, waiting for Route here: a dashed plum ring.
    if (pick) {
      const sx = cam.toScreenX(pick.x);
      const sy = cam.toScreenY(pick.z);
      g.lineWidth = 5 * k;
      g.strokeStyle = INK;
      g.beginPath();
      g.arc(sx, sy, 15 * k, 0, Math.PI * 2);
      g.stroke();
      g.lineWidth = 3 * k;
      g.strokeStyle = MARKER_STYLE.mark.colour;
      g.setLineDash(this.dash(6 * k, 4 * k));
      g.lineDashOffset = -time * 10;
      g.stroke();
      g.setLineDash(NO_DASH);
    }

    // You: the arrow on a pale halo, so it reads over a busy junction, with a cone the way you face.
    const px = cam.toScreenX(player.x);
    const py = cam.toScreenY(player.z);
    const pa = Math.atan2(Math.cos(player.heading), Math.sin(player.heading));
    this.drawCone(g, px, py, pa, 40 * k);
    if (this.look === 'night') {
      // a soft glow round you, so the arrow is the brightest thing on the map
      g.fillStyle = 'rgba(255,255,255,0.07)';
      g.beginPath();
      g.arc(px, py, 26 * k, 0, Math.PI * 2);
      g.fill();
    }
    g.fillStyle = pal.youHalo;
    g.strokeStyle = pal.text;
    g.globalAlpha = 0.8;
    g.lineWidth = 1.5 * k;
    g.beginPath();
    g.arc(px, py, 15 * k, 0, Math.PI * 2);
    g.fill();
    g.stroke();
    g.globalAlpha = 1;
    this.drawArrow(g, px, py, pa, 11 * k, player.tint);
    // The DOM over the map (the Find my car pill) and you: no caption under either.
    for (const r of reserve) this.claim(r.x0, r.y0, r.x1, r.y1);
    this.claim(px - 15 * k, py - 15 * k, px + 15 * k, py + 15 * k);
    const mr = ICON_DISC * k;
    for (const m of this.poiMarkers) this.claimDot(m.x, m.z, mr);
    for (const m of markers) if (!m.area && m.kind !== 'police' && !this.hidden.has(m.kind)) this.claimDot(m.x, m.z, m.kind === 'car' ? 14 * k : mr);

    // Labels, most important first; one that would land on another is left out.
    g.textAlign = 'left';
    g.textBaseline = 'middle';
    g.lineWidth = 3;
    g.strokeStyle = pal.halo;
    this.fontPx = Math.round(Math.max(12, 11 * k));
    g.font = `${this.fontPx}px ${pal.textFont}`;
    if (mark) this.label(g, `Your route: ${mark.label}`, cam.toScreenX(mark.x), 15 * k, cam.toScreenY(mark.z) - 17 * k, this.labelColour('mark'));
    for (let pass = 0; pass < 2; pass++) {
      // the goal, the suggestion and your car have had their say: now the district names take their room
      if (pass === 1) for (let i = 0; i < this.districtN; i++) this.claim(this.districtBox[i * 4]!, this.districtBox[i * 4 + 1]!, this.districtBox[i * 4 + 2]!, this.districtBox[i * 4 + 3]!);
      for (const m of markers) {
        if (!m.label || m.area || m.kind === 'mark' || this.hidden.has(m.kind)) continue;
        if ((m.kind === 'objective' || m.kind === 'next' || m.kind === 'car') !== (pass === 0)) continue;
        if (mark && Math.abs(m.x - mark.x) < 1 && Math.abs(m.z - mark.z) < 1) continue; // the mark's own caption
        const text = m.kind === 'next' ? `Next: ${m.label}` : m.label;
        const gap = m.kind === 'car' ? 16 * k : 11 * k;
        this.label(g, text, cam.toScreenX(m.x), gap, cam.toScreenY(m.z) - 8 * k, this.labelColour(m.kind));
      }
    }
    // The shops' names once you're in a little; street names along the road once you're in close.
    if (rel >= POI_NAME_REL && !this.hidden.has('poi')) {
      this.fontPx = Math.round(Math.max(11, 10 * k));
      g.font = `${this.fontPx}px ${pal.textFont}`;
      for (const poi of L.pois) {
        if (!POI_GLYPH[poi.kind]) continue;
        this.label(g, poi.name, cam.toScreenX(poi.pos.x), 11 * k, cam.toScreenY(poi.pos.z) + 1 * k, pal.poiName);
      }
    }
    if (z >= STREET_ZOOM) this.drawStreetNames(g, cam, k);
  }

  // full-map scratch (nothing allocated per draw)
  private cam: MapCamera | null = null;
  /** The minimap: the view's centre (you), for the distance to a car on the rim; off on the full map. */
  private carDistance = false;
  /** Your car's plate scale and the distance type size this draw (the minimap sizes them in CSS px). */
  private carK = 1;
  private distPx = 10;
  // the rim distance's text, font and width, remade only when they change
  private distTen = -1;
  private distText = '';
  private distFontPx = 0;
  private distFont = '';
  private distW = 0;
  /** The car your route leads to (its plate wears the flag's plum edge; no flag over it), or null. */
  private routedCar: MapMarker | null = null;
  private viewX = 0;
  private viewZ = 0;
  private readonly pt = { x: 0, y: 0 };
  private readonly fullToScreen = (wx: number, wz: number, out: { x: number; y: number }): void => {
    out.x = this.cam!.toScreenX(wx);
    out.y = this.cam!.toScreenY(wz);
  };
  private readonly rects = new Float32Array(4 * 128);
  /** The district names' boxes this draw (claimed after the goal's captions). */
  private readonly districtBox = new Float32Array(4 * DISTRICT_BOXES);
  private districtN = 0;
  private labelCount = 0;
  private fontPx = 12;
  private readonly dashA = [0, 0];
  private streets: { name: string; edges: number[] }[] | null = null;

  private dash(a: number, b: number): number[] {
    this.dashA[0] = a;
    this.dashA[1] = b;
    return this.dashA;
  }

  /** Reserve a screen rectangle for a label; false (nothing reserved) when it overlaps one already taken. */
  private claim(x0: number, y0: number, x1: number, y1: number): boolean {
    const r = this.rects;
    const n = this.labelCount;
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      if (x0 < r[o + 2]! && x1 > r[o]! && y0 < r[o + 3]! && y1 > r[o + 1]!) return false;
    }
    if (n * 4 >= r.length) return false;
    r[n * 4] = x0;
    r[n * 4 + 1] = y0;
    r[n * 4 + 2] = x1;
    r[n * 4 + 3] = y1;
    this.labelCount = n + 1;
    return true;
  }

  /** A label `gap` px right of the marker at `ax` — or left of it when it would run off the map's right edge. */
  private claimDot(x: number, z: number, r: number): void {
    const cam = this.cam!;
    const sx = cam.toScreenX(x);
    const sy = cam.toScreenY(z);
    if (sx < -r || sy < -r || sx > cam.w + r || sy > cam.h + r) return;
    this.claim(sx - r, sy - r, sx + r, sy + r);
  }

  /** A caption's colour: the marker's, lightened on a dark map; on the printed map ink, with the goal and the mark kept. */
  private labelColour(kind: MapMarker['kind']): string {
    if (this.look === 'print') return kind === 'objective' ? '#b3261e' : kind === 'mark' ? '#6b3fa0' : kind === 'friend' ? '#3d6b1e' : this.pal.text;
    return LABEL_COLOUR[kind] ?? this.pal.text;
  }

  private label(g: CanvasRenderingContext2D, text: string, ax: number, gap: number, y: number, colour: string): void {
    const cam = this.cam!;
    const h = this.fontPx;
    if (ax < -40 || ax > cam.w + 40 || y < -h || y > cam.h + h) return;
    const w = g.measureText(text).width;
    let x = ax + gap;
    if (x + w > cam.w - 4 && ax - gap - w >= 4) x = ax - gap - w;
    if (!this.claim(x - 2, y - h / 2 - 1, x + w + 2, y + h / 2 + 1)) return;
    g.strokeText(text, x, y);
    g.fillStyle = colour;
    g.fillText(text, x, y);
  }

  private drawStreetNames(g: CanvasRenderingContext2D, cam: MapCamera, k: number): void {
    const L = this.layout;
    if (!this.streets) {
      const by = new Map<string, number[]>();
      L.roads.edges.forEach((e, i) => {
        if (!e.name || e.alley) return;
        const list = by.get(e.name) ?? [];
        list.push(i);
        by.set(e.name, list);
      });
      this.streets = [...by].map(([name, edges]) => ({ name, edges }));
    }
    const nodes = L.roads.nodes;
    const fs = Math.round(Math.max(10, 9 * k));
    g.font = `${this.pal.streetFont === this.pal.textFont ? '500 ' : ''}${fs}px ${this.pal.streetFont}`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineWidth = 3;
    g.strokeStyle = this.pal.streetHalo;
    g.fillStyle = this.pal.street;
    for (const st of this.streets) {
      let best = -1;
      let bestLen = 0;
      for (const i of st.edges) {
        const e = L.roads.edges[i]!;
        const a = nodes[e.from]!.pos;
        const b = nodes[e.to]!.pos;
        const mx = cam.toScreenX((a.x + b.x) / 2);
        const my = cam.toScreenY((a.z + b.z) / 2);
        if (mx < 30 || mx > cam.w - 30 || my < 14 || my > cam.h - 14) continue;
        const len = Math.hypot(b.x - a.x, b.z - a.z) * cam.zoom;
        if (len > bestLen) {
          bestLen = len;
          best = i;
        }
      }
      if (best < 0) continue;
      const w = g.measureText(st.name).width;
      if (bestLen < w + 30) continue;
      const e = L.roads.edges[best]!;
      const a = nodes[e.from]!.pos;
      const b = nodes[e.to]!.pos;
      const mx = cam.toScreenX((a.x + b.x) / 2);
      const my = cam.toScreenY((a.z + b.z) / 2);
      let ang = Math.atan2(b.z - a.z, b.x - a.x);
      if (ang > Math.PI / 2) ang -= Math.PI;
      else if (ang < -Math.PI / 2) ang += Math.PI;
      // The name's box, axis-aligned (good enough to keep names off each other and off the markers' labels).
      const hw = (Math.abs(Math.cos(ang)) * w + Math.abs(Math.sin(ang)) * fs) / 2;
      const hh = (Math.abs(Math.sin(ang)) * w + Math.abs(Math.cos(ang)) * fs) / 2;
      if (!this.claim(mx - hw, my - hh, mx + hw, my + hh)) continue;
      g.save();
      g.translate(mx, my);
      g.rotate(ang);
      g.strokeText(st.name, 0, 0);
      g.fillText(st.name, 0, 0);
      g.restore();
    }
  }

  /** Your mark: a plum flag on a pin, so it reads apart from the job's red diamond (shape and colour). */
  private drawFlag(g: CanvasRenderingContext2D, x: number, y: number, k: number, colour: string): void {
    g.lineWidth = 2 * k;
    g.strokeStyle = INK;
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x, y - 24 * k);
    g.stroke();
    g.fillStyle = colour;
    g.beginPath();
    g.moveTo(x, y - 24 * k);
    g.lineTo(x + 15 * k, y - 18.5 * k);
    g.lineTo(x, y - 13 * k);
    g.closePath();
    g.fill();
    g.stroke();
    g.fillStyle = PAPER;
    g.beginPath();
    g.arc(x, y, 3.5 * k, 0, Math.PI * 2);
    g.fill();
    g.stroke();
  }

  /** Rings round every marker of the key line just tapped (they fade over FLASH_SECONDS). */
  private drawFlashes(
    g: CanvasRenderingContext2D,
    cam: MapCamera,
    markers: readonly MapMarker[],
    mark: { x: number; z: number } | null,
    player: MapPlayer,
    flash: { kind: LegendItem['kind']; glyph?: string; age: number },
    k: number,
  ): void {
    const t = flash.age;
    if (t >= FLASH_SECONDS) return;
    g.strokeStyle = MUSTARD;
    g.lineWidth = 3.5 * k;
    g.globalAlpha = 1 - t / FLASH_SECONDS;
    this.flashR = (14 + ((t * 1.6) % 1) * 18) * k;
    if (flash.kind === 'you') this.ring(g, cam, player.x, player.z);
    else if (flash.kind === 'mark') {
      if (mark) this.ring(g, cam, mark.x, mark.z);
    } else {
      for (const m of this.poiMarkers) if (legendMatches(flash, m)) this.ring(g, cam, m.x, m.z);
      for (const m of markers) if (legendMatches(flash, m)) this.ring(g, cam, m.x, m.z);
    }
    g.globalAlpha = 1;
  }

  private flashR = 0;
  private ring(g: CanvasRenderingContext2D, cam: MapCamera, x: number, z: number): void {
    g.beginPath();
    g.arc(cam.toScreenX(x), cam.toScreenY(z), this.flashR, 0, Math.PI * 2);
    g.stroke();
  }

  /** Places a tap on the full map snaps to: the shops, people with work, the objective (by their caption). */
  tapCandidates(markers: readonly MapMarker[], out: TapCandidate[]): TapCandidate[] {
    out.length = 0;
    if (!this.hidden.has('poi')) for (const poi of this.layout.pois) if (POI_GLYPH[poi.kind]) out.push({ x: poi.pos.x, z: poi.pos.z, label: poi.name });
    for (const m of markers) {
      if (m.area || !m.label || this.hidden.has(m.kind) || m.kind === 'police' || m.kind === 'hazard' || m.kind === 'mark' || m.kind === 'crew' || m.kind === 'gang' || m.kind === 'player') continue;
      out.push({ x: m.x, z: m.z, label: m.label });
    }
    return out;
  }
}

/** A rounded rectangle path (roundRect isn't in every canvas the tests run). */
function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  g.beginPath();
  g.moveTo(x + r, y);
  g.lineTo(x + w - r, y);
  g.arcTo(x + w, y, x + w, y + r, r);
  g.lineTo(x + w, y + h - r);
  g.arcTo(x + w, y + h, x + w - r, y + h, r);
  g.lineTo(x + r, y + h);
  g.arcTo(x, y + h, x, y + h - r, r);
  g.lineTo(x, y + r);
  g.arcTo(x, y, x + r, y, r);
  g.closePath();
}

/** The car your mark sits on (within 2 m): the plate carries the route's plum edge instead of a flag over it. */
function routedCar(markers: readonly MapMarker[]): MapMarker | null {
  let mark: MapMarker | null = null;
  for (const m of markers) if (m.kind === 'mark' && !m.area) mark = m;
  if (!mark) return null;
  for (const m of markers) if (m.kind === 'car' && Math.abs(m.x - mark.x) < 2 && Math.abs(m.z - mark.z) < 2) return m;
  return null;
}

/** Your car's plate on the minimap: at least this wide (CSS px), and its distance at least this tall. */
const MINI_CAR_CSS = 22;
const MINI_DIST_CSS = 9;
/** The route line on the minimap: at least this wide (CSS px). */
const MINI_ROUTE_CSS = 2.4;
/** The heading cone under your arrow: this wide either side of the way you face, in a faint paper. */
const CONE_HALF = (26 * Math.PI) / 180;
const CONE = 'rgba(233,224,201,0.16)';

/** Radius (× k) of a disc marker that carries a map icon; the plain disc is 6.6. */
const ICON_DISC = 8.6;
/** The same on the minimap… */
const ICON_DISC_MINI = 11;
/** …but never under this many CSS px (a phone's minimap is under 100 CSS px across: the icons need room). */
const MINI_ICON_CSS_R = 7.5;
/** At most this many icon discs on the minimap at once (docs/UI.md "Map icons": declutter). */
const MINI_ICONS = 5;
/** At most this many district names on screen at once (their boxes are kept for the captions). */
const DISTRICT_BOXES = 16;
/** Seconds a key line's markers flash after a tap on it. */
export const FLASH_SECONDS = 2.4;
/** Street names show from this zoom (CSS px per metre): one + from where the map opens on a phone. */
const STREET_ZOOM = 1.5;
/** The shops' names show from this zoom, relative to "the whole borough fits". */
const POI_NAME_REL = 1.6;
const NO_DASH: number[] = [];
const NO_RESERVE: { x0: number; y0: number; x1: number; y1: number }[] = [];
/** Caption colours on the full map: the marker's colour, lightened to read as text on the dark map. */
const LABEL_COLOUR: Partial<Record<MapMarker['kind'], string>> = {
  objective: '#ff9a85',
  mark: '#dcc0f4',
  car: PAPER,
  next: PAPER,
  friend: '#c9e08f',
  event: '#f0c96a',
  crew: '#9fe0d4',
  gang: '#9fe0d4',
  player: '#d8d0bc',
  hazard: '#ffad74',
};
